import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  LibraryFolderListParams,
  LibraryFolderWriteInput,
  LibraryItemListParams,
  LibraryItemUpdateInput,
} from './types';

/** Query-key factories — every library key derives from these. */
export const libraryKeys = {
  all: ['library'] as const,
  itemLists: () => [...libraryKeys.all, 'items'] as const,
  itemList: (params: LibraryItemListParams) => [...libraryKeys.itemLists(), params] as const,
  itemDetails: () => [...libraryKeys.all, 'item'] as const,
  itemDetail: (publicId: string) => [...libraryKeys.itemDetails(), publicId] as const,
  folderLists: () => [...libraryKeys.all, 'folders'] as const,
  folderList: (params: LibraryFolderListParams) => [...libraryKeys.folderLists(), params] as const,
  usage: () => [...libraryKeys.all, 'usage'] as const,
};

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export function useLibraryFolders(params: LibraryFolderListParams = {}) {
  return useQuery({
    queryKey: libraryKeys.folderList(params),
    queryFn: () => api.getLibraryFolders(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LibraryFolderWriteInput) => api.createLibraryFolder(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: libraryKeys.folderLists() }),
  });
}

export function useUpdateLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: LibraryFolderWriteInput }) =>
      api.updateLibraryFolder(publicId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: libraryKeys.folderLists() }),
  });
}

/** Deleting a folder cascades to its sub-folders and orphans their items — the
 *  item lists have to be refetched, not just the tree. */
export function useDeleteLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteLibraryFolder(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: libraryKeys.folderLists() });
      void qc.invalidateQueries({ queryKey: libraryKeys.itemLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export function useLibraryItems(
  params: LibraryItemListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: libraryKeys.itemList(params),
    queryFn: () => api.getLibraryItems(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useLibraryItem(publicId: string) {
  return useQuery({
    queryKey: libraryKeys.itemDetail(publicId),
    queryFn: () => api.getLibraryItem(publicId),
    enabled: Boolean(publicId),
  });
}

/** Uploads are called imperatively (progress reporting) — this hook exists for
 *  the cache refresh that follows one. */
export function useLibraryUploaded() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: libraryKeys.itemLists() });
    void qc.invalidateQueries({ queryKey: libraryKeys.folderLists() });
    void qc.invalidateQueries({ queryKey: libraryKeys.usage() });
  };
}

export function useUpdateLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: LibraryItemUpdateInput }) =>
      api.updateLibraryItem(publicId, input),
    onSuccess: item => {
      qc.setQueryData(libraryKeys.itemDetail(item.public_id), item);
      void qc.invalidateQueries({ queryKey: libraryKeys.itemLists() });
    },
  });
}

export function useDeleteLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteLibraryItem(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: libraryKeys.itemLists() });
      void qc.invalidateQueries({ queryKey: libraryKeys.usage() });
    },
  });
}

/** Staff-only storage line. */
export function useLibraryUsage(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: libraryKeys.usage(),
    queryFn: () => api.getLibraryUsage(),
    enabled: options.enabled,
  });
}
